// src/app/hooks/ahu/mutations.ts
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import useMutation from '../store';
import {
  useCreateDMountMutation,
  useCreateInternalWallMutation,
  useCreateSectionMutation,
  useDeleteAhuMutation,
  useUpdateAccessoryPositionMutation,
  useUpdateAhuMutation,
  useUpdateDMountMutation,
  useUpdateInternalWallMutation,
  useUpdateSectionMutation,
} from '../../services';
import {
  addDmount,
  addInternalWall,
  addSection,
  removeAhu,
  setAccessoryPosition,
  setAhu,
  setDmount,
  setInternalWall,
  setSection,
} from '../../slices';

import { IChanges, IDmount, IInternallWall, ISection, IOnChangeFunc } from '../../models';
import type { IState } from '../../../../models';

export const useUpdateAhu = () => {
  const { ahu_id = '' } = useParams();
  const dispatch = useDispatch();
  const [changes, setChanges] = useState<IChanges>({});

  const ahu = useSelector((state: IState) => state.ahu.ahus[ahu_id]);
  const { data: ahuData } = useMutation(ahu_id, changes, useUpdateAhuMutation);

  useEffect(() => {
    if (ahuData) dispatch(setAhu(ahuData));
  }, [ahuData]);

  const onChange: IOnChangeFunc = useCallback((value, name) => {
    name && setChanges({ [name]: value });
  }, []);

  return { ahu, onChange };
};

export const useUpdateSectionCabinet = (
  section_id: number,
  section: ISection,
  func?: any
) => {
  const dispatch = useDispatch();
  const [changes, setChanges] = useState<IChanges>({});
  const [currentSection, setCurrentSection] = useState(section);

  const { data } = useMutation(section_id, changes, useUpdateSectionMutation);

  useEffect(() => {
    if (data) {
      dispatch(setSection({ section: data, id: section_id }));
      setCurrentSection(data);
      func?.();
    }
  }, [data]);

  const onChange: IOnChangeFunc = useCallback((value, name) => {
    name && setChanges({ [name]: value });
  }, []);

  return { section: currentSection, onChange };
};

export const useCreateDmount = (section_id: number) => {
  const dispatch = useDispatch();
  const [createDmount, { data: dmount, isLoading }] = useCreateDMountMutation();

  useEffect(() => {
    if (dmount) {
      dispatch(addDmount({ dmount, section_id }));
    }
  }, [dmount]);

  return { dmount, isLoading, createItem: createDmount };
};

export const useUpdateDmount = (
  section_id: number,
  dmount_id: number,
  dmount: IDmount
) => {
  const dispatch = useDispatch();
  const [changes, setChanges] = useState<IChanges>({});
  const [currentState, setCurrentState] = useState(dmount);

  const { data } = useMutation(dmount_id, changes, useUpdateDMountMutation);

  useEffect(() => {
    if (data) {
      dispatch(setDmount({ dmount: data, section_id, id: dmount_id }));
      setCurrentState(data);
    }
  }, [data]);

  const onChange: IOnChangeFunc = useCallback((value, name) => {
    name && setChanges({ [name]: value });
  }, []);

  return { dmount: currentState, onChange };
};

export const useCreateInternalWall = (section_id: number) => {
  const dispatch = useDispatch();
  const [createIntWall, { data: internalWall, isLoading }] =
    useCreateInternalWallMutation();

  useEffect(() => {
    if (internalWall) {
      dispatch(addInternalWall({ internalWall, section_id }));
    }
  }, [internalWall]);

  return { internalWall, isLoading, createItem: createIntWall };
};

export const useCreateSection = (tunnel_id: number) => {
  const dispatch = useDispatch();
  const [createSection, { data: section, isLoading }] =
    useCreateSectionMutation();

  useEffect(() => {
    if (section) {
      dispatch(addSection({ section, tunnel_id }));
    }
  }, [section, tunnel_id]);

  return { section, isLoading, createItem: createSection };
};

export const useUpdateInternalWall = (
  section_id: number,
  int_wall_id: number,
  int_wall: IInternallWall
) => {
  const dispatch = useDispatch();
  const [changes, setChanges] = useState<IChanges>({});
  const [currentState, setCurrentState] = useState(int_wall);

  const { data } = useMutation(
    int_wall_id,
    changes,
    useUpdateInternalWallMutation,
    int_wall.plane
  );

  useEffect(() => {
    if (data) {
      dispatch(
        setInternalWall({ internalWall: data, section_id, id: int_wall_id })
      );
      setCurrentState(data);
    }
  }, [data]);

  const onChange: IOnChangeFunc = useCallback((value, name) => {
    name && setChanges({ [name]: value });
  }, []);

  return { int_wall: currentState, onChange };
};

const needToFetchFields = [
  'mounting_plane_id',
  'x',
  'y',
  'default_pos_y',
  'default_pos_x',
];

export const useUpdateAccessoryPosition = (
  accessory_id: number,
  getAccessory: (id: number) => void
) => {
  const dispatch = useDispatch();
  const position = useSelector(
    (state: IState) => state.ahu.accessories_position[accessory_id]
  );
  
  const [fetchAccessory, setFetchAccessory] = useState(false);
  const [changes, setChanges] = useState<IChanges>({});

  const [currentState, setCurrentState] = useState(position);

  const { data } = useMutation(
    accessory_id,
    changes,
    useUpdateAccessoryPositionMutation
  );

  useEffect(() => {
    setCurrentState(position);
  }, [position]);

  useEffect(() => {
    if (data) {
      dispatch(setAccessoryPosition({ position: data, id: accessory_id }));
      setCurrentState(data);
      if (fetchAccessory) {
        getAccessory(accessory_id);
        setFetchAccessory(false);
      }
    }
  }, [data]);

  const onChange: IOnChangeFunc = useCallback(
    (value, name) => {
      if (name) {
        setChanges({ [name]: value, id: accessory_id });
        if (needToFetchFields.includes(name)) setFetchAccessory(true);
      }
    },
    [needToFetchFields]
  );

  return { position: currentState || {}, onChange };
};
export const useDeleteAHU = () => {
  const dispatch = useDispatch();
  const [deleteAHU, { isLoading }] = useDeleteAhuMutation();
  const deleteAhu = async (ahu_id: number, signal?: AbortSignal) => {
    try {
      await deleteAHU({ id: ahu_id, signal }).unwrap();
      // Remove AHU from Redux store after successful deletion
      dispatch(removeAhu(ahu_id));
    } catch (error) {
      // Re-throw error so caller can handle it
      throw error;
    }
  };
  return { deleteAHU: deleteAhu, isLoading };
};